/**
 * Vitrine d'équipe publique (/equipe)
 * Présentation premium des coachs & assistants (parents / image).
 */
import { useEffect, useState } from "react";
import { API } from "../lib/api";
import axios from "axios";
import { Loader2, Award, GraduationCap } from "lucide-react";

const pub = axios.create({ baseURL: API });

export default function EquipePublic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pub.get("/coach-cockpit/public/team")
      .then((r) => setData(r.data))
      .catch(() => setData({ coaches: [], assistants: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="animate-spin text-amber-400" size={32} /></div>;

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="equipe-public">
      {/* Hero */}
      <div className="border-b border-zinc-900 bg-gradient-to-b from-zinc-900 to-zinc-950">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-3">Institut Impact Élite Martial</div>
          <h1 className="text-4xl sm:text-5xl font-bold">Notre équipe</h1>
          <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
            Des instructeurs passionnés qui font progresser chaque élève à son rythme, dans un cadre sécuritaire et inspirant.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <Section title="Coachs principaux" people={data.coaches} />
        {data.assistants?.length > 0 && (
          <div className="mt-14"><Section title="Assistants & relève" people={data.assistants} assistant /></div>
        )}
        {(!data.coaches?.length && !data.assistants?.length) && (
          <p className="text-center text-zinc-500 py-16">L'équipe sera présentée ici prochainement.</p>
        )}
      </div>

      <div className="border-t border-zinc-900 py-8 text-center text-zinc-600 text-sm">
        Système KAIZEN · Institut Impact Élite Martial · Val-d'Or
      </div>
    </div>
  );
}

function Section({ title, people, assistant }) {
  return (
    <div>
      <h2 className="text-lg md:text-lg font-semibold text-amber-300 uppercase tracking-wide mb-6">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {people.map((p) => (
          <div key={p.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden hover:border-amber-700/50 transition-colors" data-testid={`team-card-${p.id}`}>
            <div className="aspect-[4/3] bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
              {p.photo_url ? (
                <img src={p.photo_url} alt={p.full_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-red-600 flex items-center justify-center text-3xl font-bold">
                  {(p.display_name || p.full_name || "?")[0]}
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="font-bold text-lg">{p.full_name}</div>
              <div className="text-amber-400 text-sm">{p.title || (assistant ? "Assistant" : "Instructeur")}</div>
              <div className="flex flex-wrap gap-1 mt-2">
                {(p.disciplines || []).map((d) => (
                  <span key={d} className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{d.replace(/_/g, " ")}</span>
                ))}
                {p.years_experience ? <span className="text-[10px] px-2 py-0.5 rounded bg-amber-900/40 text-amber-300 flex items-center gap-1"><Award size={10} />{p.years_experience} ans</span> : null}
              </div>
              {p.bio_short && <p className="text-zinc-300 text-sm mt-3 leading-relaxed">{p.bio_short}</p>}
              {(p.assigned_groups || []).length > 0 && (
                <div className="mt-3 text-xs text-zinc-500 flex items-center gap-1">
                  <GraduationCap size={13} className="text-amber-400" />
                  {p.assigned_groups.join(" · ")}
                </div>
              )}
              {p.philosophy && <p className="text-zinc-400 text-xs italic mt-3 border-l-2 border-amber-700/60 pl-3">"{p.philosophy}"</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
